import { Badge } from "@/components/ui/badge";

export const DiscountBadge = ({ price, listino }) => {
  if (!price || !listino || listino <= 0 || price >= listino) return null;
  const pct = Math.round(((listino - price) / listino) * 100);
  return <span className="text-[9px] font-mono text-zinc-500 ml-1">-{pct}%</span>;
};

/**
 * Rende il prezzo "scontato cliente" se disponibile (clientPrice).
 * - Listino sbarrato se il netto cliente è inferiore
 * - Badge PROMO se la sorgente è "promo"
 * Ritorna null se non c'è una scontistica significativa.
 */
const ClientNetBlock = ({ clientPrice, listino }) => {
  if (!clientPrice) return null;
  const net = Number(clientPrice.price || 0);
  if (net <= 0) return null;
  const isPromo = clientPrice.source === "promo";
  return (
    <div className={`flex items-center justify-between rounded px-1.5 py-0.5 -mx-1.5 ${isPromo ? "bg-red-500/15" : "bg-cyan-500/10"}`}>
      <span className={`text-[10px] font-semibold tracking-wide uppercase ${isPromo ? "text-red-400" : "text-cyan-400"}`}>
        {isPromo ? "PROMO" : "Tuo prezzo"}
      </span>
      <span className={`text-sm font-extrabold tracking-tight ${isPromo ? "text-red-400" : "text-cyan-400"}`}>
        {net.toFixed(2)}
        <DiscountBadge price={net} listino={listino} />
      </span>
    </div>
  );
};

export const PriceDisplay = ({ product, userRole, clientPrice = null, lastPrice = null }) => {
  const listino = product.price_list || 0;
  const isPromoProduct = product.price_promo && product.price_promo > 0;
  const promoMargin = isPromoProduct && product.price_purchase > 0
    ? (((product.price_promo - product.price_purchase) / product.price_promo) * 100).toFixed(1)
    : null;

  // Per il cliente, abbiamo un prezzo netto risolto se ha promo/regole/manuale
  const isClientPromo = userRole === "client" && clientPrice && clientPrice.source === "promo" && clientPrice.price > 0;
  const clientNet = userRole === "client" && clientPrice && clientPrice.price > 0 && clientPrice.price < listino
    ? clientPrice
    : null;
  // Storico prezzi: ultimo prezzo pagato (mostrato solo per cliente, e solo se NON c'è una promo cliente attiva)
  const showLastPrice = userRole === "client" && !isClientPromo && lastPrice && Number(lastPrice.ultimo_prezzo) > 0;

  return (
    <div className="space-y-0.5">
      {/* PROMO catalogo (price_promo del prodotto) - prevale */}
      {isPromoProduct && (
        <div className="flex items-center justify-between bg-red-500/20 rounded px-1.5 py-0.5 -mx-1.5">
          <span className="text-[10px] text-red-400 font-semibold tracking-wide uppercase">PROMO</span>
          <span className="text-sm font-extrabold text-red-400 tracking-tight">
            {product.price_promo.toFixed(2)}
            {userRole === "admin" && promoMargin !== null && (
              <span className="text-[9px] font-normal text-red-300 ml-1">({promoMargin}%)</span>
            )}
            <DiscountBadge price={product.price_promo} listino={listino} />
          </span>
        </div>
      )}

      {/* Tuo prezzo cliente (basato su promo cliente / regole / manuale) */}
      {!isPromoProduct && clientNet && <ClientNetBlock clientPrice={clientNet} listino={listino} />}

      {/* Ultimo prezzo pagato (storico) — solo per cliente, no promo attiva */}
      {!isPromoProduct && !clientNet && showLastPrice && (
        <div className="flex items-center justify-between rounded px-1.5 py-0.5 -mx-1.5 bg-amber-500/10">
          <span className="text-[10px] font-semibold tracking-wide uppercase text-amber-400">
            Ultimo prezzo
          </span>
          <span className="text-sm font-extrabold tracking-tight text-amber-400">
            {Number(lastPrice.ultimo_prezzo).toFixed(2)}
            <DiscountBadge price={Number(lastPrice.ultimo_prezzo)} listino={listino} />
          </span>
        </div>
      )}

      {/* Listino - sbarrato se c'è uno sconto/storico attivo */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-zinc-400">Listino</span>
        <span className={`font-mono text-sm font-semibold ${(isPromoProduct || clientNet || showLastPrice) ? 'text-zinc-500 line-through' : 'text-white'}`}>
          {listino.toFixed(2)}
        </span>
      </div>

      {userRole === "admin" && product.price_purchase > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-emerald-500">Costo</span>
          <span className="font-mono text-xs text-emerald-500">{product.price_purchase.toFixed(2)}</span>
        </div>
      )}
      {!isPromoProduct && (
        <>
          {userRole === "client" && !clientNet && product.price_discounted && product.price_discounted > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-cyan-400">Riservato</span>
              <span className="font-mono text-sm font-bold text-cyan-400">
                {product.price_discounted.toFixed(2)}
                <DiscountBadge price={product.price_discounted} listino={listino} />
              </span>
            </div>
          )}
          {(userRole === "admin" || userRole === "agent") && product.price_min && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-amber-400">Min</span>
              <span className="font-mono text-sm font-bold text-amber-400">
                {product.price_min.toFixed(2)}
                <DiscountBadge price={product.price_min} listino={listino} />
              </span>
            </div>
          )}
          {(userRole === "admin" || userRole === "agent") && product.price_auth && product.price_auth > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-red-500">Autorizzare</span>
              <span className="font-mono text-xs text-red-500">
                {product.price_auth.toFixed(2)}
                <DiscountBadge price={product.price_auth} listino={listino} />
              </span>
            </div>
          )}
          {(userRole === "admin" || userRole === "agent") && product.price_discounted && product.price_discounted > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-cyan-400">Scontato</span>
              <span className="font-mono text-xs text-cyan-400">
                {product.price_discounted.toFixed(2)}
                <DiscountBadge price={product.price_discounted} listino={listino} />
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const RoleBadge = ({ role }) => {
  const styles = {
    admin: "bg-purple-500/20 text-purple-400",
    agent: "bg-blue-500/20 text-blue-400",
    client: "bg-emerald-500/20 text-emerald-400",
  };
  const labels = { admin: "Admin", agent: "Agente", client: "Cliente" };
  return <Badge className={styles[role]}>{labels[role]}</Badge>;
};
